import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../utils/ApiService';
import { message } from 'antd';
import {ProjectOption} from "../types/ProjectOption";
import { signalSplashReady } from "../utils/splashReady";

interface ProjectContextProps {
    selectedProject: string | null;
    setSelectedProject: (project: string) => void;
    projectOptions: ProjectOption[];
    managers: string[];
    selectedManager: string | null;
    selectedManagerNo: string | null;
    setSelectedManager: (manager: string | null) => void;
    setSelectedManagerNo: (managerNo: string | null) => void;
    filteredProjects: ProjectOption[];
}

export const ProjectContext = createContext<ProjectContextProps | undefined>(undefined);

export const ProjectProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [selectedProject, setSelectedProject] = useState<string | null>("");
    const [selectedManager, setSelectedManager] = useState<string | null>("");
    const [selectedManagerNo, setSelectedManagerNo] = useState<string | null>("");
    const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
    const [filteredProjects, setFilteredProjects] = useState<ProjectOption[]>([]);
    const [managers, setManagers] = useState<string[]>([]);

    useEffect(() => {
        const loadProjectOptions = async () => {
            try {
                const options = await ApiService.fetchProjectOptions();
                setProjectOptions(options);
                // Der Rahmen steht: die Projektliste haengt an der Auswahl oben links und wird
                // auf JEDER Route geladen. Das Startbild wartet darauf (ausser auf der Startseite).
                signalSplashReady("projekte");
                const uniqueManagers = Array.from(new Set(options.map((p) => p.managerName)));
                setManagers(["Alle", ...uniqueManagers]);
                if (options.length > 0) {
                    setFilteredProjects(options);
                    setSelectedProject((prev) => prev ?? options[0].number);
                    setSelectedManager((prev) => prev ?? options[0].managerName);
                    setSelectedManagerNo((prev) => prev ?? options[0].managerNo);
                }
            } catch (error) {
                message.error("Error fetching project options.");
                // Hebt die Mindestdauer auf, damit die Meldung nicht hinter dem Startbild bleibt.
                signalSplashReady("fehler");
            }
        };
        loadProjectOptions();
    }, []);

    useEffect(() => {
        if (!selectedManager || selectedManager === "Alle") {
            setFilteredProjects(projectOptions);
            setSelectedManagerNo("Alle");
        } else {
            const filtered = projectOptions.filter((p) => p.managerName === selectedManager);
            setFilteredProjects(filtered);
            setSelectedManagerNo(filtered.length > 0 ? filtered[0].managerNo : null);
        }
    }, [selectedManager, projectOptions]);


    return (
        <ProjectContext.Provider
            value={{
                selectedProject,
                setSelectedProject,
                projectOptions,
                managers,
                selectedManager,
                selectedManagerNo,
                setSelectedManager,
                setSelectedManagerNo,
                filteredProjects,
            }}
        >
            {children}
        </ProjectContext.Provider>
    );
};

export const useProjectContext = () => {
    const context = useContext(ProjectContext);
    if (!context) {
        throw new Error("useProjectContext must be used within a ProjectProvider");
    }
    return context;
};
